import { action, computed, observable, reaction } from 'mobx';
import { cloneDeep, isEmpty } from 'lodash';
import { customFont } from '@honeybook/hbui/dist/styles/fonts';
import type { RootStore } from '~/stores/root.store';
import {
    ImportFlowComponentsScreen,
    ISidebarScreenConfig,
    SidebarScreen
} from '~/components/Builder/BuilderSidebar/builder-sidebar.types';
import {
    IConfigureModalProps,
    ICreationLimitDialogDetails,
    IDraggableItem,
    IIsolatedBlockEditorState,
    IPrintOrder,
    RegainControlModalReasons,
    TBlock,
    TCompositionLayoutType
} from '~/types/builder.types';
import { IUIRouterState } from '~/types/angular.types';
import { IContractDropdownField } from '~/types/server.types';
import {
    IFlowTemplate,
    IVendorEmail,
    TFlowContext,
    TInvoiceSectionType
} from '~/types/model.types';
import { scrollToBlock } from '~/utils/flow.utils';
import { GeneralOutboundEvents } from '~/components/Builder/Mobile/builderMobileApi.enums';
import { trackError } from '~/services/logger/error.utils';

type TSelectedBlockChangedCallback = (newBlockId: SidebarScreen) => void;

export class BuilderUiStore {
    rootStore: RootStore;

    @observable selectedFlowId: string;
    @observable selectedPageId: string;
    @observable selectedBlockId: string;
    @observable selectedDisplayRootParentBlockId: string;
    @observable selectedBlockTemplate: IFlowTemplate;
    @observable selectedWorkspaceId: string;
    @observable isPageSettingsSelected: boolean;
    @observable isNavigationBlockSelected: boolean;
    @observable canvasWidth: number = Infinity;
    @observable selectedSidebarScreen: SidebarScreen;
    @observable selectedSidebarScreenConfig: ISidebarScreenConfig;
    // When this flag is turned on, all validation errors will appear
    @observable showErrors = false;
    @observable showBackButtonOnFlowReusableSidebar = true;
    @observable shareEmail: IVendorEmail = {
        subject: '',
        html_body: '',
        image_attachments: [],
        file_attachments: [],
        time_to_send: null
    };
    @observable isConfigureModalOpen: boolean = false;
    @observable configureModalProps: IConfigureModalProps = null;
    @observable isAddParticipantModalOpen: boolean = false;
    @observable isUploadFontModalOpen: boolean = false;
    @observable openServiceSelectionSubItemsModalMap: {
        [blockId: string]: boolean;
    } = {};
    @observable shownPlusId: string = null;
    @observable isResizingImage: boolean = false;
    @observable isResizingColumn: boolean = false;
    @observable shouldStopCalcHover: boolean = false; // formerly known as isInCreateMode - stops the hover calculations and hides pluses
    @observable previousAngularRoute: IUIRouterState = undefined;
    @observable previousRoute: string = undefined;
    @observable selectedSmartFieldID: string;
    @observable optionalFields: IContractDropdownField[];
    @observable showScheduleOverlayForId: string = null;
    @observable showContractOverlayForId: string = null;
    @observable showInvoiceOverlayForId: string = null;
    @observable showPagesOverlayForId: string = null;
    @observable creationLimitDialogDetails: ICreationLimitDialogDetails;
    @observable showServicesOverlayForId: string = null;
    @observable draggingItem: IDraggableItem = null;
    @observable showWelcomeTourModal: boolean = false;
    @observable showShareTabTour = null;
    @observable showResponsesTabTour = null;
    @observable showFirstFlowCongratsModal = false;
    @observable hoverOnAuthBanner = 0;
    @observable selectedBlockDnDRef = null;
    @observable isPublishChangesShareModalOpen: boolean = false;
    @observable isMobileModalOpen: boolean = false;
    @observable isResponsesMobileModalOpen: boolean = false;
    @observable isolatedBlockEditorState: IIsolatedBlockEditorState = {};
    @observable printOrder: IPrintOrder = null;
    @observable _recentFonts: string[] = [];
    @observable selectedInternalElement: { [blockId: string]: string } = {};
    @observable approvedCriticalChangeDialogBlockMap: {
        [blockId: string]: boolean;
    } = {};
    @observable regainControlModalOpenReason: RegainControlModalReasons;
    @observable isWaitForUnsavedChangesModalOpen: boolean = false;
    @observable flowToImportFromId: string;
    @observable showRecurringDiscoverabilityTourTooltip: boolean = false;
    @observable showInvoiceAwarenessOncePerSession: boolean = true;
    @observable isSessionConfigDisableOpen: boolean = false;
    @observable isPublishChangesBeforeExitModalOpen: boolean;
    @observable isContractAddedOrChanged: boolean = false;
    @observable isScheduleRequireToPayModalOpen: boolean = false;
    @observable isSchedulerOnboarding: boolean = false;
    @observable isContractOnboarding: boolean = false;
    @observable isClientflowSetupGuide: boolean = false;
    @observable progressBarPercentage: number = 0;
    @observable invoiceSidebarExpandedSectionId: TInvoiceSectionType;
    @observable showExtraTourStep: boolean = false;
    @observable changedPaymentScheduleDialogProps: {
        isOpen: boolean;
        isRecurringInvoice: boolean;
    } = null;
    @observable initialSidebarLayoutType: TCompositionLayoutType;

    @observable
    private selectedSidebarScreenChangedCallbacks: Array<TSelectedBlockChangedCallback> =
        [];

    @observable shouldShowAdminAccessFirstTimeModal: boolean = false;

    @computed
    get builderContext(): TFlowContext {
        if (!this.selectedFlow) {
            return null;
        }
        if (this.selectedFlow.isLeadForm) {
            return 'publicFlow';
        }
        if (this.selectedFlow.isInstance) {
            return 'flowInstance';
        }
        if (this.selectedFlow.isTemplate) {
            return 'flowTemplate';
        }
        trackError({
            error: new Error(
                `Unknown builder context for flowId ${this.selectedFlowId}`
            )
        });
    }

    @action.bound
    setShowExtraTourStep(isShow: boolean) {
        this.showExtraTourStep = isShow;
    }

    @action.bound
    setBuilderProgressBar(state: number) {
        this.progressBarPercentage = state ?? 0;
    }

    @action.bound
    setInvoiceSidebarExpandedSectionId(expandedId: TInvoiceSectionType) {
        this.invoiceSidebarExpandedSectionId = expandedId;
    }

    @action.bound
    setPrintOrder(printOrder: IPrintOrder) {
        this.printOrder = printOrder;
    }

    @computed
    get isNewNavbarExperience() {
        return (
            this.selectedFlow?.isLeadForm &&
            this.rootStore.userStore.currUser.isNewNavbarExperience
        );
    }

    @computed
    get isRegainControlModalOpen(): boolean {
        return !!this.regainControlModalOpenReason;
    }

    @computed
    get showBuildTabTour() {
        return (
            this.rootStore.tourStore.isTourOn('ONBOARDING_BUILD_TAB') ||
            this.rootStore.tourStore.isTourOn(
                'ONBOARDING_BUILDER_INVOICE_INTENT'
            ) ||
            this.rootStore.tourStore.isTourOn(
                'ONBOARDING_BUILDER_CONTRACT_INTENT'
            )
        );
    }

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    @action.bound
    setShowInvoiceAwarenessOncePerSession(value: boolean) {
        this.showInvoiceAwarenessOncePerSession = value;
    }

    @action.bound
    setSelectedInternalElement(blockId: string, element: string) {
        this.selectedInternalElement = {
            [blockId]: element
        };
    }

    @action.bound
    setSelectedBlockDnDRef(ref) {
        if (ref?.current) {
            this.selectedBlockDnDRef = ref.current;
            return;
        }
        this.selectedBlockDnDRef = null;
    }

    @action.bound
    setCreationLimitDialogDetails(details: ICreationLimitDialogDetails | null) {
        if (details === null) {
            this.creationLimitDialogDetails = {
                ...this.creationLimitDialogDetails,
                isOpen: false
            };
            return;
        }
        this.creationLimitDialogDetails = { ...details, isOpen: true };
    }

    // overlays - for template choosing
    @action.bound
    setShowContractOverlayForId(id: string) {
        this.showContractOverlayForId = id;
    }

    @action.bound
    setShowScheduleOverlayForId(id: string) {
        this.showScheduleOverlayForId = id;
    }

    @action.bound
    setIsPublishChangesBeforeExitModalOpen(value: boolean) {
        this.isPublishChangesBeforeExitModalOpen = value;
    }

    @action.bound
    setIsScheduleRequireToPayModalOpen(value: boolean) {
        this.isScheduleRequireToPayModalOpen = value;
    }

    @action.bound
    setIsSchedulerOnboarding(value: boolean) {
        this.isSchedulerOnboarding = value;
    }

    @action.bound
    setIsContractOnboarding(value: boolean) {
        this.isContractOnboarding = value;
    }

    @action.bound
    setIsClientflowSetupGuide(value: boolean) {
        this.isClientflowSetupGuide = value;
    }

    @action.bound
    setShowInvoiceOverlayForId(id: string) {
        this.showInvoiceOverlayForId = id;
    }

    @action.bound
    setShowRecurringDiscoverabilityTourTooltip(value: boolean) {
        this.showRecurringDiscoverabilityTourTooltip = value;
    }

    @action.bound
    setApprovedCriticalChangeDialogForBlockId(blockId: string) {
        this.approvedCriticalChangeDialogBlockMap = {
            ...this.approvedCriticalChangeDialogBlockMap,
            [blockId]: true
        };
    }

    @action.bound
    resetBuilderTab() {
        this.setShowInvoiceAwarenessOncePerSession(true);
        this.approvedCriticalChangeDialogBlockMap = {};
    }

    @action.bound
    setShowServicesOverlayForId(id: string) {
        this.showServicesOverlayForId = id;
    }

    @action.bound
    setShowPagesOverlayForId(id: string) {
        this.showPagesOverlayForId = id;
    }

    @action.bound
    resetOverlays() {
        this.setShowContractOverlayForId(null);
        this.setShowInvoiceOverlayForId(null);
        this.setShowServicesOverlayForId(null);
        this.setShowPagesOverlayForId(null);
        this.setShowScheduleOverlayForId(null);
    }

    @action.bound
    setDraggingItem(item: IDraggableItem) {
        this.draggingItem = item;
    }

    @action.bound
    setShowWelcomeTourModal(value) {
        this.showWelcomeTourModal = value;
    }

    @action.bound
    setShowShareTabTour(value) {
        this.showShareTabTour = value;
    }

    @action.bound
    setHoverOnAuthBanner() {
        this.hoverOnAuthBanner += 1;
    }

    @action.bound
    setShowFirstFlowCongratsModal(value) {
        this.showFirstFlowCongratsModal = value;
    }

    @computed
    get isShowResponsesTabTour() {
        return !!this.showResponsesTabTour;
    }

    @action.bound
    setShowResponsesTabTour(value) {
        this.showResponsesTabTour = value;
    }

    @action.bound
    resetCreateMode() {
        this.shouldStopCalcHover = false;
        this.shownPlusId = null;
    }

    @action.bound
    setShouldStopCalcHover(state: boolean) {
        this.shouldStopCalcHover = state;
    }

    @action.bound
    setIsResizingImage(state: boolean) {
        this.isResizingImage = state;
    }

    @action.bound
    setIsResizingColumn(state: boolean) {
        this.isResizingColumn = state;
    }

    @action.bound
    setShownPlusId(id: string) {
        this.shownPlusId = id;
    }

    @action.bound
    setIsConfigureModalOpen(isOpen: boolean, props?: IConfigureModalProps) {
        this.isConfigureModalOpen = isOpen;
        this.configureModalProps = props;
    }

    @action.bound
    setIsAddParticipantModalOpen(mode: boolean) {
        this.isAddParticipantModalOpen = mode;
    }

    @action.bound
    setIsUploadFontModalOpen(mode: boolean) {
        this.isUploadFontModalOpen = mode;
    }

    @action.bound
    setIsMobileModalOpen(mode: boolean) {
        this.isMobileModalOpen = mode;
    }

    @action.bound
    setIsResponsesMobileModalOpen(mode: boolean) {
        this.isResponsesMobileModalOpen = mode;
    }

    @action.bound
    setIsPublishChangesShareModalOpen(value: boolean) {
        this.isPublishChangesShareModalOpen = value;
    }

    @action.bound
    setOpenServiceSelectionSubItemsModalMap(blockId: string, mode: boolean) {
        this.openServiceSelectionSubItemsModalMap = {
            [blockId]: mode
        };
    }

    @action.bound
    refreshSidebar() {
        this.selectPageSettings(false);
        this.selectNavigationBlock(false);
        this.setSelectedSidebarScreen(null);
        this.setInitialSidebarLayoutType(null);
    }

    @action.bound
    selectFlow(id?: string) {
        this.selectedFlow?.disposeReactions();
        this.selectedFlowId = id;
        this.registerFlowReactions();
    }

    registerFlowReactions() {
        // register reaction for when user has resolved all flow errors
        this.selectedFlow?.reactions.push(
            reaction(
                () => this.selectedFlow?.hasErrors,
                hasErrors => {
                    if (hasErrors !== undefined && !hasErrors) {
                        this.setShowErrors(false);
                        this.rootStore.eventEmitterCtrl.trigger(
                            GeneralOutboundEvents.FlowValidationOnly,
                            {
                                isValid: true,
                                shouldPublish: false,
                                blocksErrors: {}
                            }
                        );
                    }
                }
            ),
            reaction(
                () => this.selectedFlow?.isDirty,
                dirty => {
                    if (dirty !== undefined) {
                        this.rootStore.eventEmitterCtrl.trigger(
                            GeneralOutboundEvents.IsDirtyFlow,
                            {
                                isDirty: dirty
                            }
                        );
                    }
                }
            )
        );
    }

    @action.bound
    selectWorkspace(id?: string) {
        this.selectedWorkspaceId = id;
    }

    @action.bound
    setSelectedSmartFieldID(fieldId: string) {
        this.selectedSmartFieldID = fieldId;
    }

    @action.bound
    selectPage(id?: string) {
        if (this.selectedPageId !== id) {
            this.selectedPageId = id;
        }
        this.selectedBlockId = null;

        if (!this.selectedPage) {
            this.selectedPageId = this.selectedFlow?.pages[0].page_id;
        }
    }

    @action.bound
    selectAndScrollToBlock({
        block,
        focusBlock = false,
        overridePageId,
        sidebarScreen
    }: {
        block: TBlock;
        focusBlock?: boolean;
        overridePageId?: string;
        sidebarScreen?: SidebarScreen;
    }) {
        const targetPageId = overridePageId || block.page_id;
        const isCurrPage = targetPageId === this.selectedPageId;

        this.selectPage(targetPageId);

        if (focusBlock) {
            this.selectBlock(block.block_id);
            if (sidebarScreen) {
                this.setSelectedSidebarScreen(sidebarScreen);
            }
        }
        // timeout used to throw the function after the event loop finishes -
        // since we want to select the page first, and only then scroll to block (when visible)
        setTimeout(() =>
            scrollToBlock({
                blockId: block.block_id,
                isSmooth: isCurrPage,
                isMobileApp: this.rootStore.eventEmitterCtrl.isMobile
            })
        );
    }

    @action.bound
    selectAndScrollToInvoiceBlock(sidebarScreen?: SidebarScreen) {
        this.selectAndScrollToBlock({
            block: this.selectedFlow.invoiceContainerBlock,
            focusBlock: true,
            sidebarScreen
        });
    }

    @action.bound
    selectAndScrollToContractBlock(sidebarScreen?: SidebarScreen) {
        this.selectAndScrollToBlock({
            block: this.selectedFlow.contractBlocks[0],
            focusBlock: true,
            sidebarScreen
        });
    }

    @action.bound
    selectAndScrollToInvoiceBlockOnReusableSidebar() {
        this.selectAndScrollToInvoiceBlock(
            ImportFlowComponentsScreen.flowComponentsForBlock
        );
    }

    @action.bound
    selectAndScrollToScheduleBlock() {
        this.selectAndScrollToBlock({
            block: this.selectedFlow.scheduleContainerBlock,
            focusBlock: true
        });
    }

    @action.bound
    selectBlock(id?: string, displayRootParentBlock?: string) {
        this.setSelectedSidebarScreen(null);
        this.selectPageSettings(false);
        this.selectedBlockId = id;
        this.selectedDisplayRootParentBlockId = displayRootParentBlock || null;
    }

    @action.bound
    selectBlockTemplate(template: IFlowTemplate) {
        this.selectedBlockTemplate = template;
    }

    @action.bound
    reset() {
        this.selectFlow(null);
        this.selectPage(null);
        this.selectBlock(null);
        this.selectBlockTemplate(null);
        this.setSelectedSidebarScreen(null);
        this.setInitialSidebarLayoutType(null);
        this.selectNavigationBlock(false);
        this.setShareEmail({
            subject: '',
            html_body: '',
            image_attachments: [],
            file_attachments: []
        });
        this.setShowInvoiceAwarenessOncePerSession(true);
        this.setIsContractAddedOrChanged(false);
        this.setIsSchedulerOnboarding(false);
        this.setIsContractOnboarding(false);
    }

    @action.bound
    selectNavigationBlock(select?: boolean) {
        this.isNavigationBlockSelected = Boolean(select);
    }

    @action.bound
    selectPageSettings(select?: boolean) {
        this.isPageSettingsSelected = Boolean(select);
    }

    @action.bound
    setCanvasWidth({ width }) {
        this.canvasWidth = width;
    }

    @action.bound
    setSidebarScreenConfig(config: ISidebarScreenConfig) {
        this.selectedSidebarScreenConfig = config;
    }

    @action.bound
    setSelectedSidebarScreen(value: SidebarScreen = null) {
        this.selectedSidebarScreen = value;

        this.selectedSidebarScreenChangedCallbacks.forEach(x => x(value));
    }

    @action.bound
    setInitialSidebarLayoutType(compositionLayoutType: TCompositionLayoutType) {
        this.initialSidebarLayoutType = compositionLayoutType;
    }

    @action.bound
    setShowBackButtonOnFlowReusableSidebar(value: boolean) {
        this.showBackButtonOnFlowReusableSidebar = value;
    }

    @action.bound
    setShowErrors(showErrors: boolean) {
        this.showErrors = showErrors;
    }

    @action.bound
    setPreviousAngularRoute(route: IUIRouterState) {
        this.previousAngularRoute = route;
    }

    @action.bound
    setPreviousRoute(pathname: string) {
        this.previousRoute = pathname;
    }

    @action.bound
    setShareEmail(email: Partial<IVendorEmail>) {
        Object.keys(this.shareEmail).forEach(key => {
            if (key in email) {
                if (key === 'time_to_send' && email?.time_to_send) {
                    this.shareEmail.time_to_send = {
                        hour:
                            email.time_to_send?.hour ||
                            this.shareEmail.time_to_send?.hour,
                        day:
                            email.time_to_send?.day ||
                            this.shareEmail.time_to_send?.day
                    };
                } else {
                    this.shareEmail[key] = email[key];
                }
            }
        });
    }

    @action
    private setEmail(currEmail: IVendorEmail, newEmail: Partial<IVendorEmail>) {
        Object.keys(currEmail).forEach(key => {
            if (key in newEmail) {
                currEmail[key] = newEmail[key];
            }
        });
    }

    @action.bound
    setOptionalFields(fields) {
        this.optionalFields = fields;
    }

    @action.bound
    setIsolatedBlockEditorState(state: Partial<IIsolatedBlockEditorState>) {
        if (isEmpty(state)) {
            this.isolatedBlockEditorState = {};
            return;
        }
        this.isolatedBlockEditorState = {
            ...this.isolatedBlockEditorState,
            prevSelectedBlockId: this.selectedBlockId,
            ...state
        };
    }

    @action.bound
    addRecentFont(fontName: string) {
        if (!fontName) {
            return;
        }

        let fontType = fontName?.replaceAll("'", '');

        // find the right type for the font
        // if fontName is the font_family
        if (!this.allFonts[fontType]) {
            fontType = Object.values(this.allFonts).find(
                f => f.font_family.replaceAll("'", '') === fontType
            )?.font_type;
        }

        if (fontType && !this._recentFonts.includes(fontType)) {
            this._recentFonts = [fontType, ...this._recentFonts.slice(0, 2)];
        }
    }

    @action.bound
    setRegainControlModalOpenReason(openReason: RegainControlModalReasons) {
        this.regainControlModalOpenReason = openReason;
    }

    @action.bound
    setWaitForUnsavedChangesModalOpen(isOpen: boolean) {
        this.isWaitForUnsavedChangesModalOpen = isOpen;
    }

    @action.bound
    setFlowToImportFromId(id: string) {
        this.flowToImportFromId = id;
    }

    @action.bound
    setIsSessionConfigDisableOpen(isOpen: boolean) {
        this.isSessionConfigDisableOpen = isOpen;
    }

    @computed
    get selectedFlow() {
        return this.rootStore.flowStore.flows[this.selectedFlowId];
    }

    @computed
    get selectedPage() {
        return this.selectedFlow?.draftRevision.pagesMap[this.selectedPageId];
    }

    @computed
    get selectedBlock() {
        return this.selectedFlow?.draftRevision.blocksMap[this.selectedBlockId];
    }

    @computed
    get selectedDisplayRootParentBlock() {
        return this.selectedFlow?.draftRevision.blocksMap[
            this.selectedDisplayRootParentBlockId
        ];
    }

    @computed
    get selectedWorkspace() {
        return this.rootStore.workspaceStore.workspaces[
            this.selectedWorkspaceId
        ];
    }

    @computed
    get workspaceMembersWithoutCurrUser() {
        return (
            this.selectedWorkspace?.members?.filter(
                member => member._id !== this.rootStore.userStore.currUser._id
            ) || []
        );
    }

    @computed
    get isResizingAction() {
        return this.isResizingImage || this.isResizingColumn;
    }

    @computed
    get isPagesNavActive() {
        return (
            !this.selectedBlock &&
            !this.isNavigationBlockSelected &&
            !this.isPageSettingsSelected
        );
    }

    @computed
    get allFonts() {
        const fonts = cloneDeep(customFont);
        this.rootStore.companyStore.currCompany.companyFonts.forEach(font => {
            if (!fonts[font.name]) {
                fonts[font.name] = {
                    font_type: font.name,
                    font_family: font.name,
                    font_url: font.url
                };
            }
        });

        return fonts;
    }

    @computed
    get recentFonts() {
        return this._recentFonts;
    }

    @computed
    get recipientsForEmailPreview() {
        return this.selectedFlow?.flowRecipientsNames.join(', ');
    }

    @computed
    get isInNewPageScreen() {
        return !!this.showPagesOverlayForId;
    }

    @action.bound
    onSelectedSidebarScreenChanged(callback: TSelectedBlockChangedCallback) {
        this.selectedSidebarScreenChangedCallbacks.push(callback);

        return () => this.unregisterSelectedSidebarScreenChanged(callback);
    }

    @action.bound
    unregisterSelectedSidebarScreenChanged(
        callback: TSelectedBlockChangedCallback
    ) {
        this.selectedSidebarScreenChangedCallbacks =
            this.selectedSidebarScreenChangedCallbacks.filter(
                x => x !== callback
            );
    }

    @action.bound
    selectPagesLevelSidebar() {
        this.refreshSidebar();
        this.selectBlock(null);
    }

    @action.bound
    selectFirstPage() {
        this.selectPage(this.selectedFlow?.pages[0].page_id);
    }

    @action.bound
    selectInvoicePage() {
        if (!this.selectedPage?.isInvoicePage) {
            this.selectPage(this.selectedFlow?.pageWithInvoice.page_id);
        }
    }

    @action.bound
    selectLastPage() {
        const pages = this.selectedFlow?.pages;
        if (pages) {
            this.selectPage(pages[pages.length - 1].page_id);
        }
    }

    @action.bound
    setIsContractAddedOrChanged(hasChange: boolean) {
        this.isContractAddedOrChanged = hasChange;
    }

    @action.bound
    setChangedPaymentScheduleDialogProps(props: {
        isOpen: boolean;
        isRecurringInvoice: boolean;
    }) {
        this.changedPaymentScheduleDialogProps = props;
    }

    @action.bound
    setShouldShowAdminAccessFirstTimeModal(value: boolean) {
        this.shouldShowAdminAccessFirstTimeModal = value;
    }
}
